import { useEffect } from 'react'
import { useDebtStore } from '@/store'
import { useSimulationStore } from '@/store'
import { useUIStore } from '@/store'
import { useRepoVersion } from '@/repository/repoVersionStore'
import { isPaidOff } from '@/engine/paidOff'
import type { Debt } from '@/types'

/** Primary facade for all debt CRUD — components never touch the store directly */
export function useDebts() {
  const debts = useDebtStore((s) => s.debts)
  const isLoading = useDebtStore((s) => s.isLoading)
  const isInitialized = useDebtStore((s) => s.isInitialized)
  const initialize = useDebtStore((s) => s.initialize)
  const initError = useDebtStore((s) => s.initError)
  const retryInitialize = useDebtStore((s) => s.retryInitialize)
  const refresh = useDebtStore((s) => s.refresh)
  const addDebt = useDebtStore((s) => s.addDebt)
  const updateDebt = useDebtStore((s) => s.updateDebt)
  const removeDebt = useDebtStore((s) => s.removeDebt)
  const bulkImport = useDebtStore((s) => s.bulkImport)
  const bulkAppend = useDebtStore((s) => s.bulkAppend)
  const markPaidOff = useDebtStore((s) => s.markPaidOff)
  const unmarkPaidOff = useDebtStore((s) => s.unmarkPaidOff)
  const runSimulations = useSimulationStore((s) => s.runSimulations)
  const extraMonthlyPayment = useUIStore((s) => s.extraMonthlyPayment)
  // v0.8.35 — subscribe to repoVersion so `bindReposToSession.resetStores()`
  // (which bumps the version) re-triggers initialize against the now-different
  // backing repo. Pre-fix on Chrome tab-restore the cloud rebind raced the
  // mount: the effect fired once against the still-local repo, resetStores
  // wiped the store, and nothing re-fired the initialize call — UI sat on
  // empty data until the next manual reload. Mirrors v0.8.24's useRecoveryAnchor
  // fix one layer down at the store-facade level.
  const repoVersion = useRepoVersion()

  useEffect(() => {
    void initialize()
  }, [initialize, repoVersion])

  // Re-run simulations whenever debts or extra payment change
  // v0.8.57 — also run on an empty list so the Strategy Engine clears
  // instead of showing the last non-empty result.
  useEffect(() => {
    runSimulations(debts, extraMonthlyPayment)
  }, [debts, extraMonthlyPayment, runSimulations])

  const optimizationDebts = debts.filter((d) => d.isInOptimizationEngine)
  const staticDebts = debts.filter((d) => !d.isInOptimizationEngine)
  // v0.8.41 — "paid off" predicate is the OR of an explicit stamp and a
  // zero balance, so legacy debts cleared pre-v0.8.41 still surface in
  // the Registry's Paid Off chip + the Dashboard cleared count without
  // needing a backfill.
  const paidOffDebts = debts.filter(isPaidOff)

  return {
    debts,
    optimizationDebts,
    staticDebts,
    paidOffDebts,
    isLoading,
    isInitialized,
    initError,
    retryInitialize,
    refresh,
    addDebt,
    updateDebt: (id: string, patch: Partial<Omit<Debt, 'id' | 'createdAt'>>) =>
      updateDebt(id, patch),
    removeDebt,
    bulkImport,
    bulkAppend,
    markPaidOff,
    unmarkPaidOff,
  }
}
